import React from "react";

import { Form, Icon } from "@weezevent/nacre";
import { useController, useFormContext } from "react-hook-form";
import PropTypes from "prop-types";
import classes from "./FormFieldSelect.module.scss";

/**
 * This component is an input to manage a select with multiple options.
 * It's a controlled field that deals with react-hook-form. Must be wrapped by a FormContext.
 */
const FormFieldSelect = ({
	name,
	label,
	options,
	control: controlProp,
	rules,
	onChange: onExternalChange,
	clearable = false,
	value: externalValue,
	onClear,
	...props
}) => {
	// Get control from context if not provided as prop
	const formContext = useFormContext();
	const control = controlProp || (formContext && formContext.control);

	const {
		field: { onChange, value: controllerValue },
		fieldState: { error }
	} = useController({
		name: name,
		control,
		rules
	});

	// Use external value if provided, otherwise use controller value
	const value = externalValue !== undefined ? externalValue : controllerValue;

	const onChangeValue = React.useCallback(
		(e, v) => {
			const newValue = v.value;
			onChange(newValue);
			if (onExternalChange) {
				onExternalChange(newValue);
			}
		},
		[onChange, onExternalChange]
	);

	const clearValue = React.useCallback(() => {
		onChange("");
		if (onExternalChange) {
			onExternalChange("");
		}
		if (onClear) {
			onClear();
		}
	}, [onChange, onExternalChange, onClear]);

	return (
		<Form.Field>
			{!!label && <Form.Label>{label}</Form.Label>}
			<div className={classes.formFieldSelectContainer}>
				<Form.SelectLabel
					key={`${name}-${value}`}
					name={name}
					onChange={onChangeValue}
					value={value}
					errors={!!error}
					options={options}
					className={classes.selectLabel}
					/* eslint-disable-next-line react/jsx-props-no-spreading */
					{...props}
				/>
				{clearable && value && (
					<Icon
						name="cross"
						onClick={clearValue}
						className={classes.clearIcon}
					/>
				)}
			</div>
		</Form.Field>
	);
};

FormFieldSelect.propTypes = {
	/**
	 * The name of the field. This name must be unique in the form.
	 * Used by react-hook-form.
	 */
	name: PropTypes.string.isRequired,
	/**
	 * The label of the field to display.
	 */
	label: PropTypes.node,
	control: PropTypes.object,
	options: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)),
	onChange: PropTypes.func,
	/**
	 * Whether the select field can be cleared with a cross icon.
	 */
	clearable: PropTypes.bool,
	/**
	 * External value to control the select (overrides react-hook-form value).
	 */
	value: PropTypes.any,
	/**
	 * Callback function called when the clear button is clicked.
	 */
	onClear: PropTypes.func,
	/**
	 * The object of rules to apply to validate the field.
	 * Used by react-hook-form.
	 */
	rules: PropTypes.object
};

FormFieldSelect.defaultProps = {
	label: "",
	rules: {},
	control: null,
	options: [],
	onChange: () => {},
	clearable: false,
	value: undefined,
	onClear: () => {}
};

export default FormFieldSelect;
